import type { Session, TenantBundle } from './types';
import { minutesOf } from './selectors';

/**
 * The journey builder.
 *
 * Turns a day's programme into an ordered set of decisions: one row per time
 * block, each offering the sessions that run in it. Where only one option
 * genuinely fits the attendee's interests, it is chosen for them; where several
 * do, they pick. That split is the whole point — the app suggests, the attendee
 * decides, and neither does all of it.
 *
 * All of it is pure, so both clients render identical choices and the same
 * pre-selections.
 */

export interface JourneyOption {
  session: Session;
  /** How many of the attendee's interests this session's tags hit. */
  score: number;
  /** True when the session matches at least one interest. */
  matches: boolean;
  /** Why it was offered, shown under the title. */
  reason: string;
}

/** Sentinel for a slot the attendee deliberately left empty. */
export const FREE = '__free__';

export interface JourneySlot {
  id: string;
  /** '09:00' — the block's start. */
  start: string;
  /** Clock range covered, for the row label. */
  label: string;
  options: JourneyOption[];
  /**
   * Pre-selected session id, or null when the attendee must choose.
   * Set only when exactly one option matches their interests.
   */
  preselected: string | null;
  /** True when the slot resolved itself and needs no input. */
  auto: boolean;
}

/** Blocks a day into non-overlapping time bands, one decision each. */
export function buildJourney(
  bundle: TenantBundle,
  day: string,
  interests: string[],
): JourneySlot[] {
  const sessions = bundle.sessions
    .filter((s) => s.day === day)
    .sort((a, b) => minutesOf(a.t) - minutesOf(b.t));

  if (!sessions.length) return [];

  // Group into bands: a session joins the current band if it starts before the
  // band's earliest end. That keeps genuine clashes as one decision, rather
  // than offering two sessions the attendee cannot both attend as two rows.
  const bands: Session[][] = [];
  let current: Session[] = [];
  let bandEnd = -1;

  for (const s of sessions) {
    const start = minutesOf(s.t);
    if (!current.length || start < bandEnd) {
      current.push(s);
      bandEnd = current.length === 1 ? minutesOf(s.end) : Math.min(bandEnd, minutesOf(s.end));
    } else {
      bands.push(current);
      current = [s];
      bandEnd = minutesOf(s.end);
    }
  }
  if (current.length) bands.push(current);

  return bands.map((band, i) => {
    const options: JourneyOption[] = band
      .map((session) => {
        const hits = (session.tags ?? []).filter((tag) => interests.includes(tag));
        const guests = session.who
          .map((id) => bundle.talent.find((t) => t.id === id)?.name)
          .filter(Boolean);

        let reason: string;
        if (hits.length > 1) reason = `Matches ${hits.slice(0, 2).join(' and ')}`;
        else if (hits.length === 1) reason = `Matches ${hits[0]}`;
        else if (guests.length) reason = `With ${guests[0]}`;
        else reason = session.track;

        return { session, score: hits.length, matches: hits.length > 0, reason };
      })
      // Best match first, then earliest — so the obvious pick is the top box.
      .sort((a, b) => b.score - a.score || minutesOf(a.session.t) - minutesOf(b.session.t));

    const matching = options.filter((o) => o.matches);
    // Auto-select only when the choice is genuinely unambiguous: exactly one
    // option matching their interests, or only one thing running at all.
    const auto = matching.length === 1 || options.length === 1;
    const preselected = auto ? (matching[0] ?? options[0]).session.id : null;

    const ends = band.map((s) => minutesOf(s.end));
    const start = band[0].t;
    const end = fmt(Math.max(...ends));

    return {
      id: `slot-${i}`,
      start,
      label: `${start} – ${end}`,
      options,
      preselected,
      auto,
    };
  });
}

function fmt(mins: number): string {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}`;
}

/**
 * Every slot also offers "keep this free".
 *
 * Without it, a sparse programme auto-resolves every band and the attendee
 * makes no decisions at all — which defeats the point of building the day
 * yourself. It is also the honest option at a convention: people need to eat,
 * walk between halls, and sit down. A gap is a real plan, not a missing one.
 */

/** The starting selection: every auto slot filled, every choice left open. */
export function initialSelection(slots: JourneySlot[]): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const slot of slots) out[slot.id] = slot.preselected;
  return out;
}

/** Restores a selection from previously saved session ids. */
export function selectionFromSaved(
  slots: JourneySlot[],
  savedIds: string[],
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const slot of slots) {
    const hit = slot.options.find((o) => savedIds.includes(o.session.id));
    out[slot.id] = hit ? hit.session.id : slot.preselected;
  }
  return out;
}

export function selectedIds(selection: Record<string, string | null>): string[] {
  return Object.values(selection).filter((v): v is string => !!v && v !== FREE);
}

/** Index of the first slot still awaiting a decision, or -1 when complete. */
export function firstOpenSlot(
  slots: JourneySlot[],
  selection: Record<string, string | null>,
): number {
  return slots.findIndex((slot) => !selection[slot.id]);
}

export interface JourneyProgress {
  decided: number;
  total: number;
  /** Slots the attendee had to decide themselves. */
  choices: number;
  pct: number;
}

export function journeyProgress(
  slots: JourneySlot[],
  selection: Record<string, string | null>,
): JourneyProgress {
  // A deliberate gap counts as decided; only an untouched slot is outstanding.
  const decided = slots.filter((s) => selection[s.id]).length;
  return {
    decided,
    total: slots.length,
    choices: slots.filter((s) => !s.auto).length,
    pct: slots.length ? Math.round((decided / slots.length) * 100) : 0,
  };
}

/**
 * Connector geometry for the org-chart lines.
 *
 * A single trunk drops from the row above and fans out to each option box.
 * Widths vary with how far a box sits from centre, so the fan reads as a
 * diagram rather than a comb. Coordinates are normalised 0..1 across the row's
 * width, and the renderer scales them.
 */
export interface Connector {
  /** Where the branch meets the option box, 0..1 across the row. */
  x: number;
  /** Stroke width, heavier for the nearest branch. */
  width: number;
  /** Dash length, so no two branches tick in step. */
  dash: number;
}

export function connectorsFor(count: number, trunkX = 0.5): Connector[] {
  if (count <= 0) return [];
  if (count === 1) return [{ x: trunkX, width: 1.4, dash: 3 }];

  return Array.from({ length: count }, (_, i) => {
    const x = count === 1 ? trunkX : 0.14 + (i * 0.72) / (count - 1);
    const distance = Math.abs(x - trunkX);
    return {
      x,
      // Nearer the trunk reads as the stronger branch.
      width: 1.5 - distance * 1.1,
      dash: 3 + (i % 3),
    };
  });
}
